import { Show } from '@components/Show';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { ScrollArea } from '@components/ui/scroll-area';
import { BlockError } from '@domain/schemas/block-error';
import type { EntityData } from '@domain/types/entity';
import { ContractError } from '@domain/types/errors';
import { blockErrorMessages, blockErrorValidationSuccessMessages } from '@root/helpers/constants';
import {
  contractErrorMessages,
  contractErrorValidationSuccessMessages,
} from '@root/helpers/constants/contract-error-messages';
import { formatMissingDataId } from '@root/helpers/schema';
import { cn } from 'cn';

import { EmptyValidation } from './EmptyValidation';
import ErrorAlert from './ErrorAlert';
import { LoadingSkeleton } from './LoadingSkeleton';
import { ValidationIcon } from './ValidationIcon';

interface ContractValidationChecksProps {
  blockErrors: BlockError[];
  className?: string;
  contractErrors: { type: ContractError; error: Record<string, string>[] }[];
  showBlockers?: boolean;
  showPasses?: boolean;
  showWarnings?: boolean;
  submission?: EntityData;
  hasLoadedSubmission?: boolean;
}

export default function ContractValidationChecks({
  blockErrors,
  className,
  contractErrors,
  showBlockers = true,
  showPasses = true,
  showWarnings = true,
  submission,
  hasLoadedSubmission = true,
}: ContractValidationChecksProps) {
  const numberOfMandatoryDataPointsInSchema = submission?.contract_number_of_required_data_points ?? 0;
  const numberOfMissingDataPointsInContract = (submission?.contract_missing_data ?? []).length;
  const numberOfMandatoryDataPointsInContract =
    numberOfMandatoryDataPointsInSchema - numberOfMissingDataPointsInContract;
  const missingDatapointsValid = numberOfMissingDataPointsInContract === 0;

  const hasInvalidFormError = contractErrors.some((contract) => contract.type === ContractError.InvalidForm);
  const hasBlockEmptyError = blockErrors.includes(BlockError.BlockEmpty);
  const hasDatapointEmptyError = blockErrors.includes(BlockError.DatapointEmpty);
  const hasVariationNotSelectedError = blockErrors.includes(BlockError.VariationNotSelected);
  const hasComments = !!submission?.comments?.length;
  const hasUnresolvedCommentsError = blockErrors.includes(BlockError.UnresolvedComments);

  const shouldShow = (hasError: boolean) => (hasError ? showBlockers : showPasses);

  const errorAlerts = [
    {
      errorMessage: contractErrorMessages[ContractError.InvalidForm],
      successMessage: contractErrorValidationSuccessMessages[ContractError.InvalidForm],
      isError: hasInvalidFormError,
      showAlert: shouldShow(hasInvalidFormError),
    },
    {
      errorMessage: blockErrorMessages[BlockError.BlockEmpty],
      successMessage: blockErrorValidationSuccessMessages[BlockError.BlockEmpty],
      isError: hasBlockEmptyError,
      showAlert: shouldShow(hasBlockEmptyError),
    },
    {
      errorMessage: blockErrorMessages[BlockError.DatapointEmpty],
      successMessage: blockErrorValidationSuccessMessages[BlockError.DatapointEmpty],
      isError: hasDatapointEmptyError,
      showAlert: shouldShow(hasDatapointEmptyError),
    },
    {
      errorMessage: blockErrorMessages[BlockError.VariationNotSelected],
      successMessage: blockErrorValidationSuccessMessages[BlockError.VariationNotSelected],
      isError: hasVariationNotSelectedError,
      showAlert: shouldShow(hasVariationNotSelectedError),
    },
    {
      errorMessage: blockErrorMessages[BlockError.UnresolvedComments],
      successMessage: blockErrorValidationSuccessMessages[BlockError.UnresolvedComments],
      isError: hasUnresolvedCommentsError,
      showAlert: hasComments && shouldShow(hasUnresolvedCommentsError),
      'data-testid': 'validation-comments-resolved' as const,
    },
  ] as const;

  const showMissingDatapointsSummaryAlert = missingDatapointsValid ? showPasses : showWarnings;
  const notShowingAlerts = !errorAlerts.some((a) => a.showAlert) && !showMissingDatapointsSummaryAlert;

  if (notShowingAlerts) {
    return <EmptyValidation />;
  }

  if (!hasLoadedSubmission) {
    return <LoadingSkeleton />;
  }

  return (
    <ScrollArea className={cn('w-full', className)}>
      <div className="flex flex-col gap-y-3 pr-4">
        {errorAlerts.map((alert) => (
          <ErrorAlert key={alert.errorMessage} {...alert} />
        ))}

        <Show when={showMissingDatapointsSummaryAlert}>
          <Alert variant={missingDatapointsValid ? 'valid' : 'warning'}>
            <ValidationIcon type={missingDatapointsValid ? 'valid' : 'warning'} />
            <AlertTitle>
              {numberOfMandatoryDataPointsInContract} of {numberOfMandatoryDataPointsInSchema} Mandatory datapoints are
              present in the contract.
            </AlertTitle>
          </Alert>
        </Show>

        {(submission?.contract_missing_data ?? []).map((missingData) => (
          <Show key={missingData.id} when={showWarnings}>
            <Alert variant="warning">
              <ValidationIcon type="warning" />
              <AlertTitle>
                Mandatory datapoint <b>{missingData.title}</b> is missing from the contract
              </AlertTitle>
              <AlertDescription className="capitalize">{formatMissingDataId(missingData.id)}</AlertDescription>
            </Alert>
          </Show>
        ))}
      </div>
    </ScrollArea>
  );
}
